<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  /** Required if using a click handler or href/to attribute. */
  actionLabel?: string
  href?: string
  to?: string
  /** @default 'info' */
  type?: 'info' | 'warn' | 'error' | 'promote'
}

const props = withDefaults(defineProps<Props>(), { type: 'info' })

const getComponent = computed(() => {
  if (props.to) return 'router-link'
  if (props.href) return 'a'
  return 'button'
})

const target = computed(() => (props.href ? '_blank' : undefined))
</script>

<template>
  <v-alert :value="true" :class="['vs-alert', `vs-${type}`]">
    <slot />
    <div v-if="actionLabel" class="actions">
      <component
        :is="getComponent"
        class="alert-action"
        :to="to ?? undefined"
        :href="href ?? undefined"
        :target="target"
        v-on="$attrs"
        >{{ actionLabel }}</component
      >
    </div>
  </v-alert>
</template>

<style scoped>
.vs-alert:is(.v-alert) {
  padding: var(--space-small) var(--space-base);
  font-family: var(--font-regular);
  font-size: 0.875rem;
  color: var(--text-color, var(--color-white));
  line-height: 1.2;
  word-wrap: break-word;
  word-break: break-word;
  background-color: var(--bg-color, transparent);
  border: 1px solid var(--border-color, var(--color-blue));
  border-radius: 8px;

  .vs-snack-close {
    color: var(--text-color);
  }
}

.vs-warn {
  --text-color: var(--color-grey--dark);
  --bg-color: var(--color-yellow--lighter);
  --border-color: var(--color-yellow--dark);
}

.vs-info {
  --text-color: var(--color-blue--dark);
  --bg-color: var(--color-blue--lighter);
  --border-color: var(--color-blue--light);
}

.vs-error {
  --text-color: var(--color-red--dark);
  --bg-color: var(--color-red--lighter);
  --border-color: var(--color-red--light);
}

.vs-promote {
  --text-color: var(--color-purple--dark);
  --bg-color: var(--color-purple--lighter);
  --border-color: var(--color-purple--light);
}

.actions {
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
}

.alert-action {
  padding: var(--space-small);
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--color-blue);
  line-height: 1;
  text-box: trim-both cap alphabetic;
  text-decoration: none;
  background-color: rgb(255 255 255 / var(--bg-opacity, 1));
  border-radius: 9999px;
  transition: background-color 150ms ease-in;

  &:hover {
    --bg-opacity: 0.5;
  }
}
</style>
